import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { api, imgUrl } from "../lib/api";

const COLLECTIONS = [
  { to: "/shop/rings", label: "Wedding Rings", slug: "rings" },
  { to: "/shop/ring-bands", label: "Ring Bands", slug: "ring-bands" },
  { to: "/shop/bracelets", label: "Bracelets", slug: "bracelets" },
  { to: "/shop/necklaces", label: "Necklaces", slug: "necklaces" },
  { to: "/shop/earrings", label: "Earrings", slug: "earrings" },
  { to: "/shop", label: "Shop All", slug: "all" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const [mega, setMega] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [previews, setPreviews] = useState({});
  const [showBuild, setShowBuild] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/site/settings").then(({ data }) => {
      setShowBuild(!!data?.show_build_a_ring);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (mega && Object.keys(previews).length === 0) {
      api.get("/products", { params: { limit: 40 } }).then(({ data }) => {
        const map = {};
        ["rings", "ring-bands", "bracelets", "necklaces", "earrings"].forEach((slug) => {
          const p = data.find((x) => x.category === slug);
          if (p) {
            const variant = p.variants?.[0];
            const img = variant?.images?.[0] || p.image;
            if (img) map[slug] = imgUrl(img);
          }
        });
        setPreviews(map);
      }).catch(() => {});
    }
  }, [mega, previews]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/95 border-b border-[#E5E5E5]/80 shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "bg-white/90 border-b border-transparent"
      }`}
      onMouseLeave={() => setMega(false)}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 h-20 grid grid-cols-3 items-center">
        {/* LEFT. hamburger */}
        <div className="flex items-center gap-6">
          <button
            className="text-[#0A0A0A] hover:text-[#C8A165] transition-colors"
            onClick={() => setMobile((m) => !m)}
            data-testid="hamburger-toggle"
            aria-label="Menu"
          >
            {mobile ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
          <div
            className="hidden lg:flex items-center gap-1 text-[#0A0A0A] cursor-pointer group"
            onMouseEnter={() => setMega(true)}
            data-testid="collections-trigger"
          >
            <span className="label-track transition-colors group-hover:text-[#C8A165]">Collections</span>
            <ChevronDown size={13} strokeWidth={1.6} className="transition-transform group-hover:rotate-180" />
          </div>
        </div>

        {/* CENTER. logo */}
        <Link
          to="/"
          data-testid="logo-link"
          aria-label="Dreamonds. Home"
          className="flex items-center justify-center"
        >
          <img
            src="/brand/dreamonds-logo-light.png"
            alt="Dreamonds"
            className="h-8 sm:h-10 w-auto select-none"
            draggable="false"
          />
        </Link>

        {/* RIGHT. cart */}
        <div className="flex items-center justify-end gap-6">
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative text-[#0A0A0A] hover:text-[#C8A165] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={22} strokeWidth={1.4} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-[#0A0A0A] text-white text-[10px] font-medium w-4 h-4 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* MEGA MENU (desktop) */}
      {mega && (
        <div
          data-testid="mega-menu"
          className="hidden lg:block absolute inset-x-0 top-20 bg-white border-t border-[#EFE7D8] shadow-[0_18px_40px_-20px_rgba(0,0,0,0.15)]"
          onMouseEnter={() => setMega(true)}
          onMouseLeave={() => setMega(false)}
        >
          <div className="max-w-[1400px] mx-auto px-12 py-10 grid grid-cols-12 gap-10">
            <div className="col-span-3">
              <p className="label-track text-[#C8A165] mb-6">Collections</p>
              <ul className="space-y-4">
                {COLLECTIONS.map((c) => (
                  <li key={c.to}>
                    <Link
                      to={c.to}
                      onClick={() => setMega(false)}
                      data-testid={`mega-link-${c.slug}`}
                      className="group inline-flex items-center gap-3 font-heading text-xl text-[#0A0A0A] hover:text-[#C8A165] transition-colors"
                    >
                      <span className="w-6 h-px bg-[#0A0A0A] group-hover:bg-[#C8A165] transition-colors" />
                      {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-9 grid grid-cols-5 gap-4">
              {COLLECTIONS.filter((c) => c.slug !== "all").map((c) => (
                <Link
                  key={c.slug}
                  to={c.to}
                  onClick={() => setMega(false)}
                  className="group block"
                  data-testid={`mega-tile-${c.slug}`}
                >
                  <div className="aspect-[3/4] overflow-hidden bg-[#F5F2EC]">
                    {previews[c.slug] ? (
                      <img
                        src={previews[c.slug]}
                        alt={c.label}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#F5F2EC] to-[#E7DFD2]" />
                    )}
                  </div>
                  <div className="mt-3 label-track text-[#0A0A0A] group-hover:text-[#C8A165] transition-colors">
                    {c.label}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MOBILE / HAMBURGER PANEL */}
      {mobile && (
        <div
          data-testid="mobile-menu"
          className="border-t border-[#E5E5E5] bg-white px-6 py-8 flex flex-col gap-1 max-h-[80vh] overflow-y-auto"
        >
          <p className="label-track text-[#C8A165] mb-4">Collections</p>
          {COLLECTIONS.map((c) => (
            <button
              key={c.to}
              data-testid={`mobile-nav-${c.slug}`}
              onClick={() => {
                navigate(c.to);
                setMobile(false);
              }}
              className="font-heading text-2xl text-[#0A0A0A] text-left py-3 border-b border-[#F0EDE7] hover:text-[#C8A165] transition-colors"
            >
              {c.label}
            </button>
          ))}
          <div className="mt-8">
            <p className="label-track text-[#C8A165] mb-4">Services</p>
            {showBuild && (
              <button
                onClick={() => { navigate("/build"); setMobile(false); }}
                className="font-heading text-2xl text-[#0A0A0A] text-left py-3 border-b border-[#F0EDE7] hover:text-[#C8A165] transition-colors w-full"
                data-testid="mobile-build-link"
              >
                Build Your Ring
              </button>
            )}
            <button
              onClick={() => { navigate("/book/consultation"); setMobile(false); }}
              className="font-heading text-2xl text-[#0A0A0A] text-left py-3 border-b border-[#F0EDE7] hover:text-[#C8A165] transition-colors w-full leading-tight"
              data-testid="mobile-book-link"
            >
              Create Your Dream Piece
              <span className="block text-sm text-[#737373] font-body mt-1 tracking-normal normal-case">
                Book a consultation here
              </span>
            </button>
            <button
              onClick={() => {
                navigate("/");
                setMobile(false);
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="price-match-section"]');
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 200);
              }}
              className="font-heading text-2xl text-[#0A0A0A] text-left py-3 border-b border-[#F0EDE7] hover:text-[#C8A165] transition-colors w-full leading-tight"
              data-testid="mobile-price-match-link"
            >
              Price Match Guarantee
              <span className="block text-sm text-[#737373] font-body mt-1 tracking-normal normal-case">
                10% off any retail price, guaranteed
              </span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
